import type {
  CertifiedValueByAccountBucket,
  CertifiedValueByFiscalYearBucket,
  StatusCountBucket,
} from '../../../../types/insights'

/**
 * IB-282 View 1 — pure, DB-free/fetch-free mapping helpers (Vitest-friendly by construction, same
 * posture as `dashboards/jurisdictions/analyticsMappers.ts`). Every mapper degrades independently
 * and never fabricates a value the backend didn't actually resolve.
 */

/**
 * AC1.1 KPI — total certified value across every visible filing, summed client-side across
 * jurisdictions (the exact same aggregation `reportsData.ts#aggregateCertifiedByJurisdiction`
 * already performs, just collapsed to one number instead of a per-jurisdiction breakdown).
 */
export function sumTotalCertifiedValue(buckets: ReadonlyArray<{ sumCertifiedAmount: string }>): number {
  return buckets.reduce((sum, b) => {
    const amount = Number(b.sumCertifiedAmount)
    return Number.isFinite(amount) ? sum + amount : sum
  }, 0)
}

export interface TopNRow {
  key: string
  label: string
  total: number
}

export interface TopNResult {
  rows: TopNRow[]
  /** Count of nonzero entries collapsed into the "All other" bucket — 0 means no truncation happened. */
  otherCount: number
  otherTotal: number
}

/**
 * AC1.4/AC1.6 — top-N by value + a single "All other" bucket when more than `topN` entries have
 * nonzero value. A disclosed, bounded truncation (never a silent drop of the remainder — `otherCount`/
 * `otherTotal` are always reported alongside the top rows).
 */
export function buildTopNWithOther(rows: ReadonlyArray<TopNRow>, topN: number): TopNResult {
  const nonZero = [...rows].filter((r) => r.total > 0).sort((a, b) => b.total - a.total)
  const top = nonZero.slice(0, topN)
  const rest = nonZero.slice(topN)
  const otherTotal = rest.reduce((sum, r) => sum + r.total, 0)
  return { rows: top, otherCount: rest.length, otherTotal }
}

export interface StatusCountRow {
  status: string
  label: string
  count: number
  /** 2 decimal places, 0 when the portfolio has zero visible filings (never NaN/Infinity). */
  pct: number
}

/** AC1.2 — one row per known status bucket (already zero-filled server-side) with a %-of-total sub-label. */
export function buildStatusCountRows(buckets: ReadonlyArray<StatusCountBucket>): StatusCountRow[] {
  const total = buckets.reduce((sum, b) => sum + (b.count ?? 0), 0)
  return buckets.map((b) => ({
    status: b.status,
    label: b.label,
    count: b.count,
    pct: total > 0 ? Math.round((b.count / total) * 10000) / 100 : 0,
  }))
}

export interface YoYTrendPoint {
  fiscalYearId: string
  sum: number
}

export type YoYDeltaVariant = 'favorable' | 'unfavorable' | 'neutral'

export interface YoYDelta {
  pct: number
  variant: YoYDeltaVariant
}

export interface YoYTrendResult {
  points: YoYTrendPoint[]
  /**
   * `null` means "no honest delta can be shown" — exactly 1 fiscal year of data (a 0% delta would
   * falsely assert stability a single point cannot support) OR a $0 prior year (an undefined
   * percentage change, never a fabricated Infinity/NaN). Never a float re-derivation of the
   * backend's own BigDecimal sums — `Number(...)` here is display-only arithmetic, same posture as
   * every other dashboard percentage (`format.ts`'s own header comment).
   */
  delta: YoYDelta | null
}

/**
 * AC1.3 — reshapes the `certified-value-by-fiscal-year` payload for the line/area chart and
 * computes the current-vs-prior-year %-delta. `buckets` arrive ALREADY chronologically ordered
 * (backend-resolved, `DashboardAssembler#assembleCertifiedValueByFiscalYear`) — this function never
 * re-sorts by array index or by the raw fiscal-year id string.
 */
export function buildYoYTrend(buckets: ReadonlyArray<CertifiedValueByFiscalYearBucket>): YoYTrendResult {
  const points: YoYTrendPoint[] = buckets.map((b) => ({ fiscalYearId: b.fiscalYearId, sum: Number(b.sum) }))
  if (points.length < 2) {
    return { points, delta: null }
  }
  const current = points[points.length - 1]!
  const prior = points[points.length - 2]!
  if (!Number.isFinite(current.sum) || !Number.isFinite(prior.sum) || prior.sum === 0) {
    return { points, delta: null }
  }
  const pct = ((current.sum - prior.sum) / prior.sum) * 100
  const variant: YoYDeltaVariant = pct > 0 ? 'favorable' : pct < 0 ? 'unfavorable' : 'neutral'
  return { points, delta: { pct, variant } }
}

/**
 * AC1.6 — reshapes the `certified-value-by-account` payload into `TopNRow`s. No account-name
 * label-lookup endpoint exists yet (disclosed gap, same posture as `CreditValueByJurisdictionFyWidget`'s
 * fiscal-year raw-id disclosure) — a shortened id is the best label available until that lookup ships.
 *
 * <p>S3 hardening fix (Verifier finding on PR 203, defense-in-depth) — the backend's
 * `CertifiedValueRollup.group` now filters out any bucket with a null `accountId` before it ever
 * reaches the wire, but this mapper guards independently anyway: a bucket with a falsy `accountId`
 * is skipped, never crashing on `.slice(0, 8)` of a null/undefined value.
 */
export function buildAccountRows(buckets: ReadonlyArray<CertifiedValueByAccountBucket>): TopNRow[] {
  return buckets
    .filter((b): b is CertifiedValueByAccountBucket & { accountId: string } => Boolean(b.accountId))
    .map((b) => ({
      key: b.accountId,
      label: `Account ${b.accountId.slice(0, 8)}`,
      total: Number(b.sum),
    }))
}
